/**
 * Parse a lesson's authored HTML body into sections (for the stepper) and into
 * bite-size pieces (for the Bite-Size Study mode). Ports the design's
 * `splitLesson` and `buildBites` — the lesson body is our own trusted content, so
 * it's parsed with DOMParser and rendered back verbatim.
 */
import { lessons, type TestKey } from '@/content/training';

export interface LessonSection {
  /** the section's H4 heading, or null for the opening material before the first H4 */
  title: string | null;
  /** inner HTML of the section's body nodes */
  html: string;
}

function serialize(nodes: Node[]): string {
  const wrap = document.createElement('div');
  nodes.forEach((n) => wrap.appendChild(n.cloneNode(true)));
  return wrap.innerHTML;
}

/** Split a lesson body into sections at each <h4>. */
export function splitLesson(body: string): LessonSection[] {
  const doc = new DOMParser().parseFromString(`<div>${body}</div>`, 'text/html');
  const root = doc.body.firstElementChild ?? doc.body;
  const secs: LessonSection[] = [];
  let cur: { title: string | null; nodes: Node[] } = { title: null, nodes: [] };
  root.childNodes.forEach((n) => {
    if (n.nodeType === 1 && (n as Element).tagName === 'H4') {
      if (cur.nodes.length || cur.title !== null) {
        secs.push({ title: cur.title, html: serialize(cur.nodes) });
      }
      cur = { title: (n as Element).textContent, nodes: [] };
    } else if (n.nodeType === 3 && !(n.textContent ?? '').trim()) {
      // skip whitespace-only text nodes
    } else {
      cur.nodes.push(n);
    }
  });
  if (cur.nodes.length || cur.title !== null) {
    secs.push({ title: cur.title, html: serialize(cur.nodes) });
  }
  return secs;
}

export interface Bite {
  id: string;
  test: TestKey;
  lessonId: string;
  lessonTitle: string;
  title: string;
  html: string;
}

const PIECE_MAX = 900;

/**
 * Break every lesson into comfortable ~900-char reading pieces, splitting long
 * lists. Cached after first build (content is static).
 */
let BITES: Bite[] | null = null;
export function buildBites(): Bite[] {
  if (BITES) return BITES;
  const out: Bite[] = [];
  for (const l of lessons) {
    const sections = splitLesson(l.body);
    sections.forEach((s, si) => {
      const doc = new DOMParser().parseFromString(`<div>${s.html}</div>`, 'text/html');
      const nodes = Array.from((doc.body.firstElementChild ?? doc.body).childNodes);
      const pieces: Node[][] = [];
      let curPiece: Node[] = [];
      let curLen = 0;
      const flush = () => {
        if (curPiece.length) {
          pieces.push(curPiece);
          curPiece = [];
          curLen = 0;
        }
      };
      const push = (node: Node) => {
        const len = (node.textContent ?? '').length;
        if (curLen + len > PIECE_MAX && curPiece.length) flush();
        curPiece.push(node);
        curLen += len;
      };
      nodes.forEach((node) => {
        if (
          node.nodeType === 1 &&
          (node as Element).tagName === 'UL' &&
          (node as Element).children.length > 6
        ) {
          const lis = Array.from((node as Element).children);
          for (let i = 0; i < lis.length; i += 5) {
            const ul = document.createElement('ul');
            lis.slice(i, i + 5).forEach((li) => ul.appendChild(li.cloneNode(true)));
            push(ul);
          }
        } else {
          push(node);
        }
      });
      flush();
      const title = s.title ?? 'The big picture';
      pieces.forEach((pieceNodes, pi) => {
        out.push({
          id: `${l.id}_s${si}_p${pi}`,
          test: l.test,
          lessonId: l.id,
          lessonTitle: l.title,
          title: title + (pieces.length > 1 ? ` · part ${pi + 1} of ${pieces.length}` : ''),
          html: serialize(pieceNodes),
        });
      });
    });
  }
  BITES = out;
  return out;
}
